import { useCallback, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AnimatePresence, motion } from "framer-motion"
import {
  GiCrossedSwords,
  GiWorld,
  GiScrollUnfurled,
  GiShield,
  GiStack,
} from "react-icons/gi"
import { loginLogoUrl } from "@/constants/loginLogoUrl"
import { GUARDIANS_COMIC_URL } from "@/constants/guardiansGameRules"
import { WELCOME_SLIDES } from "@/constants/welcomeSlides"
import { markWelcomeSeen } from "@/lib/welcomeSeen"

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 48 : -48,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({
    x: direction > 0 ? -48 : 48,
    opacity: 0,
  }),
}

function SlideVisual({ slideId }: { slideId: string }) {
  const iconClass = "h-14 w-14 text-guardians-accent"
  switch (slideId) {
    case "intro":
      return (
        <img
          src={loginLogoUrl}
          alt=""
          width={120}
          height={120}
          className="h-28 w-28 object-contain"
          decoding="async"
        />
      )
    case "passport":
      return <GiScrollUnfurled className={iconClass} aria-hidden />
    case "cards":
      return <GiStack className={iconClass} aria-hidden />
    case "duel":
      return <GiCrossedSwords className={iconClass} aria-hidden />
    case "city":
      return <GiWorld className={iconClass} aria-hidden />
    case "ready":
      return <GiShield className={iconClass} aria-hidden />
    default:
      return null
  }
}

export default function WelcomePage() {
  const navigate = useNavigate()
  const [index, setIndex] = useState(0)
  const [direction, setDirection] = useState(0)
  const slide = WELCOME_SLIDES[index]
  const isLast = index === WELCOME_SLIDES.length - 1

  const finish = useCallback(() => {
    markWelcomeSeen()
    navigate("/login", { replace: true })
  }, [navigate])

  function goNext() {
    if (isLast) {
      finish()
      return
    }
    setDirection(1)
    setIndex((i) => Math.min(i + 1, WELCOME_SLIDES.length - 1))
  }

  function goPrev() {
    if (index <= 0) return
    setDirection(-1)
    setIndex((i) => i - 1)
  }

  return (
    <div className="relative flex min-h-dvh flex-col overflow-hidden bg-guardians-bg">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(250,204,21,0.12),transparent)]"
        aria-hidden
      />

      <header className="relative z-10 flex items-center justify-between px-5 pt-6">
        <button
          type="button"
          onClick={finish}
          className="text-sm text-guardians-muted transition hover:text-white"
        >
          Пропустить
        </button>
        <span className="text-xs tabular-nums text-guardians-muted">
          {index + 1} / {WELCOME_SLIDES.length}
        </span>
      </header>

      <main className="relative z-10 mx-auto flex w-full max-w-md flex-1 flex-col px-5 pb-6 pt-4">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={slide.id}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
            className="flex flex-1 flex-col"
          >
            <div className="mb-8 flex min-h-[8rem] items-center justify-center">
              <SlideVisual slideId={slide.id} />
            </div>
            <h1 className="text-center text-2xl font-extrabold text-white">
              {slide.title}
            </h1>
            <div className="mt-5 space-y-3">
              {slide.lines.map((line) => (
                <p
                  key={line.slice(0, 32)}
                  className="text-center text-sm leading-relaxed text-guardians-muted"
                >
                  {line}
                </p>
              ))}
              {slide.highlight ? (
                <p className="rounded-xl border border-guardians-accent/30 bg-guardians-accent/10 px-4 py-3 text-center text-sm text-white">
                  {slide.highlight}
                </p>
              ) : null}
            </div>
            {slide.id === "ready" ? (
              <p className="mt-6 text-center text-xs text-guardians-muted">
                Комикс и материалы:{" "}
                <a
                  href={GUARDIANS_COMIC_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-guardians-accent underline decoration-guardians-accent/40"
                >
                  guardians.daks.by
                </a>
              </p>
            ) : null}
          </motion.div>
        </AnimatePresence>

        <div className="mt-auto space-y-4 pt-8">
          <div className="flex justify-center gap-2">
            {WELCOME_SLIDES.map((s, i) => (
              <button
                key={s.id}
                type="button"
                aria-label={`Слайд ${i + 1}`}
                onClick={() => {
                  setDirection(i > index ? 1 : -1)
                  setIndex(i)
                }}
                className={`h-2 rounded-full transition-all ${
                  i === index
                    ? "w-6 bg-guardians-accent"
                    : "w-2 bg-white/20 hover:bg-white/35"
                }`}
              />
            ))}
          </div>

          <div className="flex gap-3">
            {index > 0 ? (
              <button
                type="button"
                onClick={goPrev}
                className="flex-1 rounded-xl border border-white/15 py-3 text-sm font-medium text-guardians-muted transition hover:border-white/25 hover:text-white"
              >
                Назад
              </button>
            ) : (
              <div className="flex-1" />
            )}
            <button
              type="button"
              onClick={goNext}
              className="flex-[1.4] rounded-xl bg-guardians-accent py-3 text-sm font-semibold text-guardians-bg transition hover:brightness-105"
            >
              {isLast ? "Играть" : "Далее"}
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}
